/**
 * Graph Engine
 * 
 * Core graph data structure and operations.
 * Modular and reusable.
 */

import type { Graph, GraphNode, GraphEdge } from './types.js';

export class GraphEngine {
  private graph: Graph = {
    nodes: new Map(),
    edges: new Map()
  };

  /**
   * Add a node to the graph
   */
  addNode(node: GraphNode): void {
    this.graph.nodes.set(node.id, node);
  }

  /**
   * Get a node by ID
   */
  getNode(id: string): GraphNode | undefined {
    return this.graph.nodes.get(id);
  }

  /**
   * Get all nodes
   */
  getAllNodes(): GraphNode[] {
    return Array.from(this.graph.nodes.values());
  }

  /**
   * Get nodes by type
   */
  getNodesByType(type: 'container' | 'content'): GraphNode[] {
    return Array.from(this.graph.nodes.values()).filter(node => node.type === type);
  }

  /**
   * Add an edge to the graph
   */
  addEdge(edge: GraphEdge): void {
    this.graph.edges.set(edge.id, edge);
  }

  /**
   * Get all edges
   */
  getAllEdges(): GraphEdge[] {
    return Array.from(this.graph.edges.values());
  }

  /**
   * Get edges for a node
   */
  getEdgesForNode(nodeId: string): GraphEdge[] {
    return Array.from(this.graph.edges.values()).filter(
      edge => edge.from === nodeId || edge.to === nodeId
    );
  }

  /**
   * Get content nodes for a container node
   */
  getContentNodesForContainer(containerId: string): GraphNode[] {
    const membershipEdges = Array.from(this.graph.edges.values()).filter(
      edge => edge.type === 'membership' && edge.to === containerId
    );
    
    return membershipEdges
      .map(edge => this.graph.nodes.get(edge.from))
      .filter((node): node is GraphNode => node !== undefined && node.type === 'content');
  }

  /**
   * Clear all nodes and edges
   */
  clear(): void {
    this.graph.nodes.clear();
    this.graph.edges.clear();
  }

  /**
   * Get the graph structure
   */
  getGraph(): Graph {
    return this.graph;
  }
}

